import { openDB, DBSchema, IDBPDatabase } from "idb";
import * as idbKv from "idb-keyval";

import Animal from "@/models/animal.ts";
import AnimalMorbidity from "@/models/animal-morbidity.ts";
import Kraal from "@/models/kraal.ts";
import Line from "@/models/line";
import Treatment from "@/models/treatment";
import TreatmentComposition from "@/models/treatment-composition";
import MorbidityReason from "@/models/morbidity-reason";
import SpeciesCode from "@/models/species-code";
import DispensaryItem from "@/models/dispensary-item";
import ReaderLocationDescription from "@/models/reader-location-description";
import KraalMorbidity from "@/models/kraal-morbidity";
import KraalMorbidityHistory from "@/models/kraal-morbidity-history";
import TagReaderLocationInfo from "@/models/tag-reader-location-info";
import AppSettings from "@/app-settings";

//import AppSettings from "@/app-settings";

interface MyDB extends DBSchema {
  animals: {
    key: number;
    indexes: {
      "by-kraal": string;
      "by-visual-ear-tag": string;
    };
    value: Animal;
  };
  "animal-morbidities": {
    key: string;
    indexes: { "by-animal": number };
    value: AnimalMorbidity;
  };
  kraals: {
    key: string;
    indexes: { "by-line": string };
    value: Kraal;
  };
  lines: {
    key: string;
    value: Line;
  };
  treatments: {
    key: string;
    value: Treatment;
  };
  "treatment-compositions": {
    key: string;
    indexes: {
      "by-treatment": number;
      "by-dispensary-item": number;
    };
    value: TreatmentComposition;
  };
  "morbidity-reasons": {
    key: string;
    value: MorbidityReason;
  };
  "species-codes": {
    key: string;
    value: SpeciesCode;
  };
  "dispensary-items": {
    key: string;
    value: DispensaryItem;
  };
  "reader-location-descriptions": {
    key: string;
    value: ReaderLocationDescription;
  };
  "kraal-morbidities": {
    key: string;
    indexes: { "by-kraal": "KraalId" };
    value: KraalMorbidity;
  };
  "kraal-morbidity-histories": {
    key: string;
    indexes: { "by-kraal": "KraalId" };
    value: KraalMorbidityHistory;
  };
  "tag-reader-location-infos": {
    key: string;
    value: TagReaderLocationInfo;
  };
  // "app-settings": {
  //   key: string;
  //   value: string;
  // };
}

type ObjectStoreNames =
  | "animals"
  | "animal-morbidities"
  | "kraals"
  | "lines"
  | "treatments"
  | "treatment-compositions"
  | "morbidity-reasons"
  | "species-codes"
  | "dispensary-items"
  | "reader-location-descriptions"
  | "kraal-morbidities"
  | "kraal-morbidity-histories"
  | "tag-reader-location-infos";

class DataManager {
  //private _db: Promise<IDBPDatabase<MyDB>>;
  _db: Promise<IDBPDatabase<MyDB>>;

  constructor() {}

  initializeDatabase() {
    this._db = openDB<MyDB>("dimssa-field-app-DB", 1, {
      upgrade(db, oldVersion, newVersion) {
        console.log("initializeDatabase()");
        console.log(`oldVersion: ${oldVersion}`);
        console.log(`newVersion: ${newVersion}`);

        switch (newVersion) {
          case 1: {
            const animalsStore = db.createObjectStore("animals", {
              keyPath: "AnimNumb"
              //autoIncrement: true
            });
            animalsStore.createIndex("by-kraal", "KraalId");
            animalsStore.createIndex("by-visual-ear-tag", "VisualEarTag");

            const animalMorbiditiesStore = db.createObjectStore(
              "animal-morbidities",
              {
                keyPath: "animalMorbidityId",
                autoIncrement: true
              }
            );
            animalMorbiditiesStore.createIndex("by-animal", "AnimalId");

            const kraalsStore = db.createObjectStore("kraals", {
              keyPath: "KraalId"
            });
            kraalsStore.createIndex("by-line", "LineId");

            db.createObjectStore("lines", { keyPath: "LineId" });
            db.createObjectStore("treatments", { keyPath: "TreatmentInfoId" });

            const treatmentCompositionStore = db.createObjectStore(
              "treatment-compositions",
              {
                keyPath: "TreatmentCompositionId"
              }
            );
            treatmentCompositionStore.createIndex(
              "by-treatment",
              "TreatmentInfoId"
            );
            treatmentCompositionStore.createIndex(
              "by-dispensary-item",
              "DispensaryItemId"
            );

            db.createObjectStore("morbidity-reasons", {
              keyPath: "MorbidityReasonId"
            });
            db.createObjectStore("species-codes", { keyPath: "SpeciesCodeId" });
            db.createObjectStore("dispensary-items", {
              keyPath: "DispensaryItemId"
            });
            db.createObjectStore("reader-location-descriptions", {
              keyPath: "ReaderLocationId"
            });
            const kraalMorbiditiesStore = db.createObjectStore(
              "kraal-morbidities",
              {
                keyPath: "KraalMorbidityId",
                autoIncrement: true
              }
            );
            kraalMorbiditiesStore.createIndex("by-kraal", "KraalId");

            const kraalMorbidityHistoriesStore = db.createObjectStore(
              "kraal-morbidity-histories",
              {
                keyPath: "KraalMorbidityHistoryId",
                autoIncrement: true
              }
            );
            kraalMorbidityHistoriesStore.createIndex("by-kraal", "KraalId");

            db.createObjectStore("tag-reader-location-infos", {
              keyPath: "TagReaderLocationInfoId",
              autoIncrement: true
            });

            // let appSettings: AppSettings = new AppSettings();
            // appSettings.hardwareServerAddress = "pi.gigalot.systems";
            // appSettings.hardwareServerRestfulPort = "9000";
            // appSettings.hardwareServerReaderPort = "8004";

            // appSettings.onsiteServerAddress = "node.gigalot.systems";
            // appSettings.cloudServerAddress = "gigalot.systems";


            // idbKv.set("appSettings", appSettings);
          }
        }
      }
    });
  }

  addAnimals(animals: any[]) {
    this._db
      .then(function(db) {
        let tx = db.transaction("animals", "readwrite");
        let os = tx.objectStore("animals");
        animals.forEach(animal => {
          os.put(animal);
        });
        return tx.done;
      })
      .then(() => {
        console.log("animals added!");
      })
      .catch(error => {
        console.log("addAnimals error", error);
      });
  }

  addItems(objectStoreName: ObjectStoreNames, items: any[]) {
    return this._db.then(function(db) {
      let tx = db.transaction(objectStoreName, "readwrite");
      let os = tx.objectStore(objectStoreName);
      items.forEach(items => {
        os.add(items); //.put() overwrites existing keys, .add() should complain otherwise
      });
      return tx.done;
    });
    // .then(() => {
    //   console.log(`addItems to ${objectStoreName} successful!`);
    // })
    // .catch(error => {
    //   console.log(`addItems to ${objectStoreName} error`, error);
    // });
  }

  clearDatabase() {
    return this._db.then(async function(db) {
      let clearPromises: Promise<undefined>[] = [];
      for (let i = 0; i < db.objectStoreNames.length; i++) {
        clearPromises.push(db.clear(db.objectStoreNames[i]));
      }

      return Promise.all(clearPromises);
    });
  }

  getObjectStoreCount(objectStoreName: ObjectStoreNames): Promise<number> {
    return this._db.then(db => db.count(objectStoreName));
  }

  getVisualTags() {
    if (!this._db) {
      throw Error("_db unavailable");
    }
    return this._db.then(async function(db) {
      let animals = await db
        .transaction("animals")
        .store.index("by-visual-ear-tag")
        .getAll();
      let visualTags = animals.map(a => a.VisualEarTag.replace(/^0+/, "")); //remove leading 0s
      return visualTags;

      //return x.join(",")

      //let cursor = await db.transaction("animals").store.openCursor();
      // let cursor = await db.transaction("animals").store.index("by-visual-ear-tag").openKeyCursor()

      // let visualTagToAnimNumb: {[key: string]: number} = {};

      // while (cursor) {
      //   //console.log(cursor.key, cursor.value);
      //   //visualTagToAnimNumb[cursor.value.VisualEarTag] = cursor.value.AnimNumb;
      //   //console.log(cursor.key, cursor.primaryKey);
      //   if (visualTagToAnimNumb[cursor.key]) {
      //     console.log(cursor.key + " duplicate: ");
      //     console.log(visualTagToAnimNumb[cursor.key]);
      //     console.log(cursor.primaryKey);
      //   }
      //   visualTagToAnimNumb[cursor.key] = cursor.primaryKey;
      //   cursor = await cursor.continue();
      // }

      // // let animal = await db.get("animals", 82831.02)
      // // console.log("82831.02: " + animal.VisualEarTag);
      // console.log(Object.keys(visualTagToAnimNumb).length);
    });
  }

  // getAppSettings(): Promise<AppSettings> {
  //   return idbKv.get<AppSettings>("appSettings");
  // }

  // setAppSettings(appSettings: AppSettings): Promise<void> {
  //   return idbKv.set("appSettings", appSettings);
  // }
}

export { DataManager };
